import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FiCheckCircle, FiXCircle, FiLoader } from "react-icons/fi";
import mmgPaymentService from "../../services/mmgPayment";
import { useAuth } from "../../contexts/AuthContext";

const PaymentCallbackContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem;
`;

const PaymentCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 500px;
  width: 100%;
`;

const StatusIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  font-size: 2.5rem;

  ${(props) => {
    if (props.$status === "success") {
      return `
        background: #d4edda;
        color: #155724;
      `;
    } else if (props.$status === "error") {
      return `
        background: #f8d7da;
        color: #721c24;
      `;
    } else {
      return `
        background: #fff3cd;
        color: #856404;
      `;
    }
  }}
`;

const StatusTitle = styled.h1`
  color: #2d5016;
  margin-bottom: 1rem;
  font-size: 1.8rem;
  font-weight: 600;
`;

const StatusMessage = styled.p`
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
  font-size: 1.1rem;
`;

const TransactionDetails = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  text-align: left;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #2d5016;
`;

const DetailValue = styled.span`
  color: #666;
  font-family: monospace;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &.primary {
    background: #2d5016;
    color: white;

    &:hover {
      background: #1e3a0f;
      transform: translateY(-2px);
    }
  }

  &.secondary {
    background: #6c757d;
    color: white;

    &:hover {
      background: #545b62;
      transform: translateY(-2px);
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #2d5016;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [paymentStatus, setPaymentStatus] = useState("processing");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const processPaymentCallback = async () => {
      try {
        const token = searchParams.get("token");
        const error = searchParams.get("error");

        if (error) {
          setPaymentStatus("error");
          setError("Payment was cancelled or failed");
          return;
        }

        if (!token) {
          setPaymentStatus("error");
          setError("No payment token received");
          return;
        }

        // Process the payment callback
        const result = mmgPaymentService.handlePaymentCallback(token);

        if (result.success) {
          setPaymentStatus("success");
          setPaymentDetails({
            transactionId: result.transactionId,
            merchantTransactionId: result.merchantTransactionId,
            message: result.resultMessage,
          });

          // Update subscription status in backend
          try {
            // This would be implemented to update the subscription status
            console.log("Payment successful, updating subscription...");
          } catch (updateError) {
            console.error("Failed to update subscription:", updateError);
          }
        } else {
          setPaymentStatus("error");
          setError(result.resultMessage || "Payment failed");
        }
      } catch (err) {
        console.error("Payment callback error:", err);
        setPaymentStatus("error");
        setError("Failed to process payment");
      }
    };

    processPaymentCallback();
  }, [searchParams]);

  const handleContinue = () => {
    navigate("/subscriptions");
  };

  const handleRetry = () => {
    navigate("/meal-plans");
  };

  const renderContent = () => {
    if (paymentStatus === "processing") {
      return (
        <>
          <LoadingSpinner />
          <StatusTitle>Processing Payment...</StatusTitle>
          <StatusMessage>
            Please wait while we process your payment. This may take a few
            moments.
          </StatusMessage>
        </>
      );
    }

    if (paymentStatus === "success") {
      return (
        <>
          <StatusIcon $status="success">
            <FiCheckCircle />
          </StatusIcon>
          <StatusTitle>Payment Successful!</StatusTitle>
          <StatusMessage>
            Your subscription has been created successfully. You will receive a
            confirmation email shortly.
          </StatusMessage>

          {paymentDetails && (
            <TransactionDetails>
              <DetailRow>
                <DetailLabel>Transaction ID:</DetailLabel>
                <DetailValue>
                  {paymentDetails.merchantTransactionId}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>MMG Transaction ID:</DetailLabel>
                <DetailValue>{paymentDetails.transactionId}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Status:</DetailLabel>
                <DetailValue style={{ color: "#155724", fontWeight: "600" }}>
                  {paymentDetails.message}
                </DetailValue>
              </DetailRow>
            </TransactionDetails>
          )}

          <ActionButtons>
            <Button className="primary" onClick={handleContinue}>
              View My Subscriptions
            </Button>
            <Button
              className="secondary"
              onClick={() => navigate("/meal-plans")}
            >
              Browse More Meal Plans
            </Button>
          </ActionButtons>
        </>
      );
    }

    return (
      <>
        <StatusIcon $status="error">
          <FiXCircle />
        </StatusIcon>
        <StatusTitle>Payment Failed</StatusTitle>
        <StatusMessage>
          {error ||
            "We encountered an issue processing your payment. Please try again."}
        </StatusMessage>

        <ActionButtons>
          <Button className="primary" onClick={handleRetry}>
            Try Again
          </Button>
          <Button className="secondary" onClick={() => navigate("/meal-plans")}>
            Back to Meal Plans
          </Button>
        </ActionButtons>
      </>
    );
  };

  return (
    <PaymentCallbackContainer>
      <PaymentCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {renderContent()}
      </PaymentCard>
    </PaymentCallbackContainer>
  );
};

export default PaymentCallback;
